import { useCallback } from 'react';

interface WizardPersistenceData {
  stepData: Record<string, any>;
  stepCompleted: Record<string, boolean>;
  currentStep: number;
  timestamp: number;
}

interface UseWizardPersistenceReturn {
  saveData: (data: Omit<WizardPersistenceData, 'timestamp'>) => void;
  loadData: () => WizardPersistenceData | null;
  clearData: () => void;
}

/**
 * Hook for wizard data persistence
 * Handles saving and loading wizard state from localStorage
 */
export function useWizardPersistence(
  persistKey: string,
  enabled: boolean = true
): UseWizardPersistenceReturn {
  
  const saveData = useCallback((data: Omit<WizardPersistenceData, 'timestamp'>) => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    try {
      const persistData: WizardPersistenceData = {
        ...data,
        timestamp: Date.now()
      };

      localStorage.setItem(persistKey, JSON.stringify(persistData));
    } catch (error) {
      console.warn('Failed to save wizard data to localStorage:', error);
    }
  }, [persistKey, enabled]);

  const loadData = useCallback((): WizardPersistenceData | null => {
    if (!enabled || typeof window === 'undefined') {
      return null;
    }

    try {
      const savedData = localStorage.getItem(persistKey);
      if (!savedData) {
        return null;
      }

      const parsedData = JSON.parse(savedData) as WizardPersistenceData;
      
      // Check if data is not too old (e.g., 24 hours)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const isExpired = Date.now() - parsedData.timestamp > maxAge;
      
      if (isExpired) {
        localStorage.removeItem(persistKey);
        return null;
      }

      return parsedData;
    } catch (error) {
      console.warn('Failed to load wizard data from localStorage:', error);
      localStorage.removeItem(persistKey);
      return null;
    }
  }, [persistKey, enabled]);

  const clearData = useCallback(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(persistKey);
    } catch (error) {
      console.warn('Failed to clear wizard data from localStorage:', error);
    }
  }, [persistKey, enabled]);

  return {
    saveData,
    loadData,
    clearData
  };
}